import { EditorNode } from '../core/EditorNode';
import { SkillIcon } from '../utils/SkillIcon';
import { getToken } from '@/lib/token';
import { getApiBaseUrl as getApiBaseUrlFromEnv, resolveEnvironment } from '@seede/api-client';

const getApiBaseUrl = () => {
  return getApiBaseUrlFromEnv(resolveEnvironment());
};

export class SkillNode extends EditorNode {
  constructor() {
    super('userskill');
  }

  render(data, context) {
    const { makeDraggable, onClick, onRemove } = context;
    const { id, name, cover } = data;

    const container = document.createElement('span');
    container.contentEditable = 'false';
    container.setAttribute('data-type', 'userskill');
    container.setAttribute('data-skill-id', id);
    if (name) container.setAttribute('data-skill-name', name);
    if (cover) container.setAttribute('data-skill-cover', cover);
    container.setAttribute('data-value', `@VeesoUserSkill('${id}')`);

    container.className =
      'userskill-tag group relative inline-flex h-6 items-center gap-1.5 pl-1 pr-2 rounded-md bg-black/[0.03] border border-black/5 hover:border-black/10 transition-all duration-300 select-none align-middle cursor-pointer';

    if (makeDraggable) {
      makeDraggable(container);
    }

    if (onClick) {
      container.addEventListener('click', (e) => {
        if (container.classList.contains('dragging')) return;
        e.preventDefault();
        e.stopPropagation();
        onClick(data, container, e);
      });
    }

    const iconContainer = document.createElement('div');
    iconContainer.className = 'relative w-3.5 h-3.5 flex-shrink-0';

    const coverImg = document.createElement('img');
    coverImg.className =
      'absolute inset-0 w-3.5 h-3.5 object-cover rounded ring-1 ring-black/5 opacity-0 transition-opacity duration-300';
    if (cover) {
      coverImg.src = cover;
      coverImg.onload = () => {
        coverImg.classList.remove('opacity-0');
        iconPlaceholder.classList.add('opacity-0');
      };
    }

    const iconPlaceholder = document.createElement('div');
    iconPlaceholder.className =
      'w-3.5 h-3.5 flex items-center justify-center bg-white rounded ring-1 ring-black/5';
    iconPlaceholder.innerHTML = SkillIcon;
    const svg = iconPlaceholder.querySelector('svg');
    if (svg) {
      svg.setAttribute('width', '12');
      svg.setAttribute('height', '12');
      svg.classList.add('text-black/60');
    }
    if (cover) {
      iconPlaceholder.classList.add('opacity-0');
    }

    iconContainer.appendChild(coverImg);
    iconContainer.appendChild(iconPlaceholder);
    container.appendChild(iconContainer);

    const labelContainer = document.createElement('span');
    labelContainer.className =
      'text-black/80 text-xs font-medium tracking-tight truncate max-w-[120px] leading-none flex items-center';
    labelContainer.textContent = name || 'Loading...';
    container.appendChild(labelContainer);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className =
      'absolute -top-1.5 -right-1.5 w-4 h-4 bg-black text-white/90 rounded-full flex items-center justify-center shadow-md transition-all duration-200 opacity-0 group-hover:opacity-100 hover:scale-110';
    closeBtn.title = 'Remove';
    closeBtn.innerHTML = `
      <svg class="w-2 h-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12" />
      </svg>
    `;

    closeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (onRemove) {
        onRemove(data, container);
      } else {
        container.remove();
      }
    });
    container.appendChild(closeBtn);

    // Only fetch if we don't have name or cover
    if (!name || !cover) {
      this.fetchSkill(id, labelContainer, coverImg, iconPlaceholder, container);
    }

    return container;
  }

  update(element, data, context) {
    const parent = element.parentNode;
    if (!parent) return;

    const newNode = this.render(data, context);
    parent.replaceChild(newNode, element);
  }

  async fetchSkill(id, labelContainer, coverImg, iconPlaceholder, container) {
    try {
      const token = getToken();
      if (!token) {
        if (labelContainer.textContent === 'Loading...') {
          labelContainer.textContent = 'Skill';
        }
        return;
      }

      const apiBase = getApiBaseUrl();
      const response = await fetch(`${apiBase}/api/user-skills/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const skill = data.data || data;

        if (skill.name) {
          labelContainer.textContent = skill.name;
          if (container) container.setAttribute('data-skill-name', skill.name);
        } else if (labelContainer.textContent === 'Loading...') {
          labelContainer.textContent = 'Skill';
        }

        if (skill.cover) {
          coverImg.src = skill.cover;
          coverImg.onload = () => {
            coverImg.classList.remove('opacity-0');
            iconPlaceholder.classList.add('opacity-0');
          };
          if (container) container.setAttribute('data-skill-cover', skill.cover);
        }
      } else {
        if (labelContainer.textContent === 'Loading...') {
          labelContainer.textContent = 'Skill not found';
        }
      }
    } catch (error) {
      console.error('Error fetching skill in tag:', error);
      if (labelContainer.textContent === 'Loading...') {
        labelContainer.textContent = 'Skill';
      }
    }
  }

  serialize(element) {
    return element.getAttribute('data-value') || '';
  }
}
